import { GraphQLClientError } from '@/services/errors/GraphQLClientError'
import type {
  ShopSessionCreateMutation,
  ShopSessionCreateMutationVariables,
  ShopSessionOutcomeFragment,
  ShopSessionOutcomeIdQuery,
  ShopSessionOutcomeIdQueryVariables,
  ShopSessionOutcomeQuery,
  ShopSessionOutcomeQueryVariables,
  ShopSessionQuery,
  ShopSessionQueryVariables,
} from '@/services/graphql/generated'
import {
  ShopSessionCreateDocument,
  ShopSessionDocument,
  ShopSessionOutcomeDocument,
  ShopSessionOutcomeIdDocument,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import type { SimplePersister } from '@/services/persister/Persister.types'
import type { ShopSession } from '@/services/shopSession/ShopSession.types'
import type { ApolloClient } from '@apollo/client'

export class ShopSessionService {
  constructor(
    private readonly persister: SimplePersister,
    private readonly apolloClient: ApolloClient<unknown>,
  ) {}

  private createParams: ShopSessionCreateMutationVariables | null = null
  private createPromise: Promise<ShopSession> | null = null

  public shopSessionId() {
    return this.persister.fetch()
  }

  public saveId(shopSessionId: string) {
    this.persister.save(shopSessionId)
  }

  public reset() {
    this.persister.reset()
    this.createPromise = null
    this.createParams = null
  }

  public async getOrCreate(params: ShopSessionCreateMutationVariables): Promise<ShopSession> {
    const existingShopSession = await this.fetch()

    if (existingShopSession?.countryCode === params.countryCode && params.attribution?.trackingData == null) {
      return existingShopSession
    }

    // Deduplicate mutation, Apollo won't do this for us
    const paramsEquals = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
    if (!this.createPromise || !paramsEquals(this.createParams, params)) {
      this.createParams = params
      this.createPromise = this.create(params)
    }
    return await this.createPromise
  }

  public async fetch(): Promise<ShopSession | null> {
    const shopSessionId = this.persister.fetch()
    if (!shopSessionId) return null
    try {
      return await this.fetchById(shopSessionId)
    } catch {
      logger.info('ShopSession not found', { shopSessionId })
      return null
    }
  }

  public async fetchById(shopSessionId: string): Promise<ShopSession> {
    const { data } = await this.apolloClient.query<ShopSessionQuery, ShopSessionQueryVariables>({
      query: ShopSessionDocument,
      variables: { shopSessionId },
    })
    return data.shopSession
  }

  public async create(variables: ShopSessionCreateMutationVariables): Promise<ShopSession> {
    const result = await this.apolloClient.mutate<ShopSessionCreateMutation, ShopSessionCreateMutationVariables>({
      mutation: ShopSessionCreateDocument,
      variables,
      // CRITICAL: Prevent caching at all levels to stop data leaks
      // Skip apollo cache
      fetchPolicy: 'no-cache',
      context: {
        fetchOptions: {
          // Skip Next network cache
          next: { revalidate: 0 },
        },
      },
      // TODO: Investigate if we can do it by returning shopSession record on top instead of shopSessionCreate
      // QUESTION from Rafael: Why is this update function needed? I tested without it and it worked as expected, I think!
      update: (cache, result) => {
        const shopSession = result.data?.shopSessionCreate
        if (shopSession) {
          cache.writeQuery({
            query: ShopSessionDocument,
            data: { shopSession },
            variables: { shopSessionId: shopSession.id },
          })
        }
      },
    })

    const shopSession = result.data?.shopSessionCreate
    if (!shopSession) throw new GraphQLClientError('Unable to create ShopSession', 'ShopSessionCreate')

    this.saveId(shopSession.id)

    return shopSession
  }

  public async fetchOutcomeId(shopSessionId: string) {
    const { data } = await this.apolloClient.query<ShopSessionOutcomeIdQuery, ShopSessionOutcomeIdQueryVariables>({
      query: ShopSessionOutcomeIdDocument,
      variables: { shopSessionId },
    })

    return data.shopSession.outcome?.id ?? null
  }

  public async fetchOutcome(shopSessionOutcomeId: string): Promise<ShopSessionOutcomeFragment | null> {
    const { data } = await this.apolloClient.query<ShopSessionOutcomeQuery, ShopSessionOutcomeQueryVariables>({
      query: ShopSessionOutcomeDocument,
      variables: { shopSessionOutcomeId },
    })
    return data.shopSessionOutcome ?? null
  }
}
